import React, { useState, useContext, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BooksForm from './BooksForm';
import ListBookArticles from './ListBookArticles';
import {
  Button,
  Modal,
  Table,
  ButtonGroup,
  Dropdown,
  Form,
  Col,
  Row,
  InputGroup,
  Badge,
  Pagination,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import { ProductContext } from '../../ProductProvider';
import axios from 'axios';
import Preloader from '../../components/Preloader/Preloader';
import BooksPreview from './BooksPreview';
import { Helmet } from 'react-helmet-async';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useReactToPrint } from "react-to-print";
import { CSVLink } from "react-csv";
import { downloaderBookinfo, books_category } from '../../ProData';
import AutoComplete from '../../components/AutoComplete';
import { validateKeywords } from '../../utils/keywordValidator';
import './AddBooks.css';

const config = require('../../config.json');

export default function AddBooks() {
  const [bookDetails, setBookDetails] = useState({
    autoGenerate: true,
    accessionNo: "",
    bookAuthor: "",
    bookCategory: "NA",
    bookClassificationNo: "",
    bookEdition: "",
    bookPages: "",
    bookPrice: "",
    bookPublishYear: "",
    bookPublisherName: "",
    bookScope: "Circulation",
    bookLocation: "",
    bookStatus: "Active",
    bookTitle: "",
    clientID: "",
    registeredOn: "",
    updatedBy: "",
    updatedOn: "",
    authorMark: "NA",
    bookCopies: 1,
    bookPublisherLocation: "",
    bookSubTitle: "",
    bookKeywords: "",
    bookAbstract: "",
    bookDimension: "",
    isbn: ""
  });
  const [oldBookDetails, setOldBookDetails] = useState({});

  const [viewOnly, setViewOnly] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { validateLogin, loggedUser, generateDate } = useContext(ProductContext);

  // Data & Search States
  const [loaded, setLoaded] = useState(true);
  const [searchResult, setSearchResult] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchTopic, setSearchTopic] = useState('AccessionNo');
  const [dispMsg, setDispMsg] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [createdBookSuccess, setCreatedBookSuccess] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copiedAcc, setCopiedAcc] = useState('');

  // Real-time In-Page Filters
  const [clientFilter, setClientFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [scopeFilter, setScopeFilter] = useState('ALL');
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [locationFilter, setLocationFilter] = useState('ALL');

  // Sorting States
  const [sorting, setSorting] = useState({ field: 'AccessionNo', order: 'asc' });

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);

  // Batch Selection States
  const [selectedAccessions, setSelectedAccessions] = useState(new Set());

  // Modal States
  const [show, setShow] = useState(false);
  const [formType, setFormType] = useState('View');
  const [previousFormType, setPreviousFormType] = useState('New');
  const [modalTitle, setModalTitle] = useState('');
  const [previousModalTitle, setPreviousModalTitle] = useState('Catalogue New Book');
  const [imageFile, setImageFile] = useState('');
  const [profileImage, setProfileImage] = useState("images/cat1.png");
  const [newFileName, setNewFileName] = useState('');
  const [newAccessionNo, setNewAccessionNo] = useState('');
  const [categoryList, setCategoryList] = useState([]);

  // Book Articles Modal States
  const [showArticlesModal, setShowArticlesModal] = useState(false);
  const [selectedBookForArticles, setSelectedBookForArticles] = useState(null);

  // Scroll to Top State
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Refs for Print & CSV
  const csvLinkEl = useRef(null);
  const csvSelectedLinkEl = useRef(null);
  const componentRef = useRef(null);

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Initial Auth & Categories
  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    } else {
      handleReset();
      const temp = books_category.filter((item) => item.categoryDisplay === "Yes");
      setCategoryList(temp);
    }
  }, [loggedUser, validateLogin]);

  // Search API Call
  const executeSearch = useCallback(async (topic, query) => {
    try {
      setLoaded(false);
      setDispMsg('');
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = "VJCollege";
      let keysearchValue = (query || '').trim();

      if (topic !== "BookCategory" && topic !== "Call No") {
        keysearchValue = keysearchValue.toUpperCase();
      }

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/books`, {
        params: {
          fetchKey: keysearchValue || 'ALL',
          fetchOption: topic || 'AccessionNo',
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const data = Array.isArray(response.data) ? response.data : [];
      setSearchResult(data);
      setHasSearched(true);
      setDispMsg(`${data.length} Book${data.length === 1 ? '' : 's'} Found`);
      setSelectedAccessions(new Set());
      setCurrentPage(1);
      setLoaded(true);

      if (data.length > 0) {
        enqueueSnackbar(`${data.length} Book${data.length === 1 ? '' : 's'} Loaded`, { variant: 'success' });
      } else {
        enqueueSnackbar('No books found for this query', { variant: 'info' });
      }
    } catch (error) {
      console.error("Error searching books:", error);
      enqueueSnackbar("Error fetching books: " + (error.response?.data?.message || error.message), { variant: 'warning' });
      setLoaded(true);
    }
  }, [enqueueSnackbar]);

  const handleSearch = (event) => {
    if (event) event.preventDefault();
    if (!searchValue.trim() && searchTopic !== "BookCategory") {
      enqueueSnackbar('Please enter search details or select a quick category!', { variant: 'info' });
      return;
    }
    executeSearch(searchTopic, searchValue);
  };

  // Quick Preset Category Search
  const handleQuickCategory = (topic, query) => {
    setSearchTopic(topic);
    setSearchValue(query === 'ALL' ? '' : query);
    executeSearch(topic, query);
  };

  // Auto-fetch on mount to present catalogue immediately
  /*useEffect(() => {
    executeSearch('BookCategory', 'Theology');
  }, [executeSearch]);*/

  // Sorting Handler
  const handleSortKey = (field) => {
    let order = 'asc';
    if (sorting.field === field && sorting.order === 'asc') {
      order = 'desc';
    }
    setSorting({ field, order });
  };

  // Unique Lists for Filter Dropdowns
  const uniqueCategories = useMemo(() => {
    const cats = new Set();
    searchResult.forEach(item => {
      if (item.bookCategory && item.bookCategory !== 'NA') cats.add(item.bookCategory);
    });
    return Array.from(cats).sort();
  }, [searchResult]);

  const uniqueLocations = useMemo(() => {
    const locs = new Set();
    searchResult.forEach(item => {
      const loc = item.bookLocation || item.liblocation || item.location;
      if (loc) locs.add(loc);
    });
    return Array.from(locs).sort();
  }, [searchResult]);

  // KPI Dashboard Metrics
  const kpiStats = useMemo(() => {
    const total = searchResult.length;
    const active = searchResult.filter(b => (b.bookStatus || 'Active').toLowerCase() === 'active').length;
    const issued = searchResult.filter(b => (b.bookStatus || '').toLowerCase() === 'issued').length;
    const reference = searchResult.filter(b => (b.bookScope || '').toLowerCase() === 'reference').length;
    const activePct = total > 0 ? Math.round((active / total) * 100) : 0;

    return { total, active, issued, reference, activePct };
  }, [searchResult]);

  // Filtered & Sorted Books
  const processedBooks = useMemo(() => {
    let result = [...searchResult];

    // Status Filter
    if (statusFilter !== 'ALL') {
      result = result.filter(b => (b.bookStatus || 'Active') === statusFilter);
    }

    // Scope Filter
    if (scopeFilter !== 'ALL') {
      result = result.filter(b => (b.bookScope || 'Circulation') === scopeFilter);
    }

    // Category Filter
    if (categoryFilter !== 'ALL') {
      result = result.filter(b => b.bookCategory === categoryFilter);
    }

    // Shelf Location Filter
    if (locationFilter !== 'ALL') {
      result = result.filter(b => (b.bookLocation || b.liblocation || b.location) === locationFilter);
    }

    // Real-time In-Page Text Search Filter
    if (clientFilter.trim() !== '') {
      const term = clientFilter.toLowerCase();
      result = result.filter(b => {
        const acc = String(b.accessionNo || '').toLowerCase();
        const title = (b.bookTitle || '').toLowerCase();
        const sub = (b.bookSubTitle || '').toLowerCase();
        const author = (b.bookAuthor || '').toLowerCase();
        const call = (b.bookClassificationNo || '').toLowerCase();
        const cat = (b.bookCategory || '').toLowerCase();
        const pub = (b.bookPublisherName || '').toLowerCase();
        const loc = (b.bookLocation || b.liblocation || b.location || '').toLowerCase();
        const isbn = (b.isbn || '').toLowerCase();
        const kw = (b.bookKeywords || '').toLowerCase();

        return acc.includes(term) ||
          title.includes(term) ||
          sub.includes(term) ||
          author.includes(term) ||
          call.includes(term) ||
          cat.includes(term) ||
          pub.includes(term) ||
          loc.includes(term) ||
          isbn.includes(term) ||
          kw.includes(term);
      });
    }

    // Sorting
    if (sorting.field) {
      result.sort((a, b) => {
        let valA, valB;
        if (sorting.field === 'AccessionNo') {
          valA = Number(a.accessionNo) || a.accessionNo || 0;
          valB = Number(b.accessionNo) || b.accessionNo || 0;
        } else if (sorting.field === 'BookTitle') {
          valA = (a.bookTitle || '').toLowerCase();
          valB = (b.bookTitle || '').toLowerCase();
        } else if (sorting.field === 'BookAuthor') {
          valA = (a.bookAuthor || '').toLowerCase();
          valB = (b.bookAuthor || '').toLowerCase();
        } else if (sorting.field === 'CallNo') {
          valA = (a.bookClassificationNo || '').toLowerCase();
          valB = (b.bookClassificationNo || '').toLowerCase();
        } else if (sorting.field === 'bookCategory') {
          valA = (a.bookCategory || '').toLowerCase();
          valB = (b.bookCategory || '').toLowerCase();
        } else if (sorting.field === 'bookStatus') {
          valA = (a.bookStatus || '').toLowerCase();
          valB = (b.bookStatus || '').toLowerCase();
        } else {
          valA = a[sorting.field] ?? '';
          valB = b[sorting.field] ?? '';
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
        }

        if (valA < valB) return sorting.order === 'asc' ? -1 : 1;
        if (valA > valB) return sorting.order === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [searchResult, statusFilter, scopeFilter, categoryFilter, locationFilter, clientFilter, sorting]);

  // Pagination Slice
  const totalPages = Math.ceil(processedBooks.length / pageSize) || 1;
  const paginatedBooks = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return processedBooks.slice(startIndex, startIndex + pageSize);
  }, [processedBooks, currentPage, pageSize]);

  // Batch Selection Handlers
  const handleToggleSelectAll = () => {
    if (selectedAccessions.size === paginatedBooks.length && paginatedBooks.length > 0) {
      setSelectedAccessions(new Set());
    } else {
      const newSet = new Set(paginatedBooks.map(b => b.accessionNo));
      setSelectedAccessions(newSet);
    }
  };

  const handleToggleSelectOne = (accessionNo) => {
    setSelectedAccessions(prev => {
      const next = new Set(prev);
      if (next.has(accessionNo)) {
        next.delete(accessionNo);
      } else {
        next.add(accessionNo);
      }
      return next;
    });
  };

  const selectedBooksData = useMemo(() => {
    return searchResult.filter(b => selectedAccessions.has(b.accessionNo));
  }, [searchResult, selectedAccessions]);

  // Validation
  const validateFields = async () => {
    const {
      autoGenerate,
      accessionNo,
      bookAuthor,
      bookCategory,
      bookClassificationNo,
      bookCopies,
      bookPublishYear,
      bookPublisherName,
      bookScope,
      bookStatus,
      bookTitle,
      bookKeywords
    } = bookDetails;

    // Manual Accession Number Validation (only for New / Clone)
    if ((formType === "New" || formType === "Clone") && !autoGenerate) {
      const trimmedAcc = (accessionNo || '').trim();
      if (!trimmedAcc) {
        enqueueSnackbar("Accession No is mandatory for manual assignment.", { variant: 'info' });
        return "failed";
      }

      if (trimmedAcc.length < 6) {
        enqueueSnackbar("Manual Accession Number must be at least 6 characters.", { variant: 'warning' });
        return "failed";
      }

      // Backend check for manual accession number uniqueness
      try {
        const { idToken } = (await fetchAuthSession()).tokens ?? {};
        const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/books`, {
          params: {
            fetchKey: trimmedAcc.toUpperCase(),
            fetchOption: 'AccessionNo',
            clientID: 'VJCollege'
          },
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });
        const data = Array.isArray(response.data) ? response.data : [];
        const match = data.find(
          (b) => (b.accessionNo || '').toString().trim().toUpperCase() === trimmedAcc.toUpperCase()
        );
        if (match) {
          enqueueSnackbar(`Accession #${trimmedAcc} already exists in library catalogue! ("${match.bookTitle}")`, { variant: 'warning' });
          return "failed";
        }
      } catch (err) {
        console.error("Error checking accession number:", err);
      }
    }

    if (!autoGenerate && bookCopies > 1 && (formType === "New" || formType === "Clone")) {
      enqueueSnackbar("Book Copies cannot be more than 1 for manual Accession No", { variant: 'info' });
      return "failed";
    }

    if (formType === "Update") {
      const trimmedNewAcc = (newAccessionNo || '').trim();
      if (!trimmedNewAcc) {
        enqueueSnackbar("New Accession Number is required.", { variant: 'info' });
        return "failed";
      }
    }

    if (!bookTitle || !bookTitle.trim()) {
      enqueueSnackbar("Book Title is mandatory!", { variant: 'info' });
      return "failed";
    }
    if (!bookAuthor || !bookAuthor.trim()) {
      enqueueSnackbar("Author is mandatory.", { variant: 'info' });
      return "failed";
    }
    if (!bookCategory || bookCategory === "NA") {
      enqueueSnackbar("Book Category is mandatory.", { variant: 'info' });
      return "failed";
    }
    if (!bookClassificationNo || !bookClassificationNo.trim()) {
      enqueueSnackbar("Book Classification Number is mandatory.", { variant: 'info' });
      return "failed";
    }
    if (!bookPublisherName || !bookPublisherName.trim()) {
      enqueueSnackbar("Book Publisher is mandatory!", { variant: 'info' });
      return "failed";
    }
    if (!bookPublishYear || !String(bookPublishYear).trim()) {
      enqueueSnackbar("Book Publish Year is mandatory!", { variant: 'info' });
      return "failed";
    }
    if (!bookStatus) {
      enqueueSnackbar("Book Status is mandatory!", { variant: 'info' });
      return "failed";
    }
    if (!bookScope) {
      enqueueSnackbar("Book Scope is mandatory!", { variant: 'info' });
      return "failed";
    }
    if (formType === "New" && bookCopies > 10) {
      enqueueSnackbar("Can add only maximum 10 copies at a time!", { variant: 'info' });
      return "failed";
    }
    if (bookKeywords && bookKeywords.trim()) {
      const kwValidation = validateKeywords(bookKeywords, true);
      if (!kwValidation.valid) {
        enqueueSnackbar(`Keywords Error: ${kwValidation.error}`, { variant: 'warning' });
        return "failed";
      }
    }
    return "passed";
  };

  // Reset Book Form
  const handleReset = () => {
    setBookDetails({
      autoGenerate: true,
      accessionNo: "",
      bookAuthor: "",
      bookCategory: "NA",
      bookClassificationNo: "",
      bookEdition: "",
      bookPages: "",
      bookPrice: "",
      bookPublishYear: "",
      bookPublisherName: "",
      bookScope: "Circulation",
      bookLocation: "",
      bookStatus: "Active",
      bookTitle: "",
      clientID: "",
      registeredOn: "",
      updatedBy: "",
      updatedOn: "",
      authorMark: "NA",
      bookCopies: 1,
      bookPublisherLocation: "",
      bookSubTitle: "",
      bookKeywords: "",
      bookAbstract: "",
      bookDimension: "",
      isbn: ""
    });
    setDispMsg('');
    setImageFile('');
    setProfileImage("images/cat1.png");
  };

  // New Book Submit
  const handleNewSubmit = async (event) => {
    if (event) event.preventDefault();
    try {
      setLoaded(false);
      const validationResult = await validateFields();
      const { idToken } = (await fetchAuthSession()).tokens ?? {};

      if (validationResult === "passed") {
        const { autoGenerate, accessionNo, bookAuthor, bookCategory, bookClassificationNo, bookEdition, bookPages, bookPrice, bookPublishYear, bookPublisherName, bookScope, bookLocation, bookStatus, bookTitle, authorMark, bookCopies, bookPublisherLocation, bookSubTitle, bookKeywords, bookAbstract, bookDimension, isbn } = bookDetails;
        const todayDate = await generateDate();
        const tbookLocation = bookLocation || "";
        const effectiveAccessionNo = autoGenerate ? "" : (accessionNo || '').trim().toUpperCase();

        const params = {
          clientID: loggedUser.website || "VJCollege",
          accessionNo: effectiveAccessionNo,
          autoGenerate: Boolean(autoGenerate),
          registeredOn: todayDate,
          bookAuthor: bookAuthor.toUpperCase(),
          bookCategory: bookCategory,
          bookClassificationNo: bookClassificationNo,
          bookEdition: bookEdition || '1st',
          bookPages: String(bookPages || '0'),
          bookPrice: String(bookPrice || '0'),
          bookPublishYear: String(bookPublishYear || ''),
          bookPublisherName: bookPublisherName.toUpperCase(),
          bookScope: bookScope,
          bookLocation: tbookLocation,
          bookStatus: bookStatus,
          bookTitle: bookTitle.toUpperCase(),
          updatedOn: todayDate,
          updatedBy: loggedUser.nickname || "Librarian",
          authorMark: authorMark || 'NA',
          bookAbstract: (bookAbstract || '').toUpperCase(),
          bookKeywords: (bookKeywords || '').toUpperCase(),
          bookCopies: String(bookCopies ?? '1'),
          bookPublisherLocation: (bookPublisherLocation || 'NA').toUpperCase(),
          bookSubTitle: (bookSubTitle || 'NA').toUpperCase(),
          bookDimension: bookDimension || 'NA',
          isbn: (isbn && isbn !== 'NA') ? isbn.toUpperCase() : 'NA'
        };
        console.log("params : ", params);
        const response = await axios.post(`${config.api.invokeURL}/vjlibrary/catalogue`, params, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

        const rawAccessionNumbers = response.data?.accessionNumbers;
        const responseAccessionNo = Array.isArray(rawAccessionNumbers)
          ? rawAccessionNumbers
          : rawAccessionNumbers
            ? [String(rawAccessionNumbers)]
            : (effectiveAccessionNo ? [String(effectiveAccessionNo)] : []);

        const primaryAccessionNo = responseAccessionNo[0] || effectiveAccessionNo || '';

        const keywordpayload = {
          book: { ...params, accessionNo: primaryAccessionNo },
          oldBook: "",
          accessionNumbers: responseAccessionNo
        };

        await axios.post(`${config.api.invokeURL}/vjlibrary/catalogue/keyword`, keywordpayload, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

        if (imageFile) {
          postOnS3(primaryAccessionNo);
        }

        // Capture newly generated accession numbers and book details for librarian review
        const successRecord = {
          accessionNumbers: responseAccessionNo,
          primaryAccessionNo: primaryAccessionNo,
          bookTitle: params.bookTitle,
          bookAuthor: params.bookAuthor,
          bookClassificationNo: params.bookClassificationNo,
          bookCategory: params.bookCategory,
          bookLocation: params.bookLocation,
          bookScope: params.bookScope,
          bookPublisherName: params.bookPublisherName,
          bookPublishYear: params.bookPublishYear,
          bookCopies: params.bookCopies,
          registeredOn: todayDate,
          authorMark: params.authorMark,
          isbn: params.isbn,
          message: response.data?.message || "Book added successfully"
        };
        setCreatedBookSuccess(successRecord);
        setShowSuccessModal(true);

        // Prepend new book record(s) to searchResult so it immediately shows at top of the table
        const newBookEntries = responseAccessionNo.map((acc) => ({
          ...params,
          accessionNo: acc,
          registeredOn: todayDate
        }));
        setSearchResult((prev) => [...newBookEntries, ...prev]);
        setHasSearched(true);

        const accString = responseAccessionNo.join(', ');
        setDispMsg(`Book Catalogued Successfully — Assigned Accession No: ${accString}`);

        handleClose();
        handleReset();
        enqueueSnackbar(`Book catalogued successfully! Assigned Accession No: ${accString}`, {
          variant: 'success',
          autoHideDuration: 10000
        });
        setLoaded(true);
        window.scrollTo(0, 0);
      } else {
        setLoaded(true);
      }
    } catch (error) {
      console.error("Error creating book:", error);
      const serverError = error.response?.data?.error || error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || error.message;
      enqueueSnackbar("Error Occurred: " + serverError, { variant: 'warning' });
      setLoaded(true);
    }
  };

  // Edit Book Submit
  const handleEditSubmit = async (event) => {
    if (event) event.preventDefault();
    try {
      setLoaded(false);
      const validationResult = await validateFields();
      if (validationResult === "passed") {
        const { clientID, accessionNo, bookAuthor, registeredOn, bookCategory, bookClassificationNo, bookEdition, bookPages, bookPrice, bookPublishYear, bookPublisherName, bookScope, bookLocation, bookStatus, bookTitle, authorMark, bookCopies, bookPublisherLocation, bookSubTitle, bookKeywords, bookAbstract, bookDimension, isbn } = bookDetails;
        const todayDate = await generateDate();
        const { idToken } = (await fetchAuthSession()).tokens ?? {};

        const params = {
          clientID: clientID || loggedUser.website || "VJCollege",
          accessionNo: accessionNo,
          registeredOn: registeredOn || todayDate,
          bookAuthor: (bookAuthor || '').toUpperCase(),
          bookCategory: bookCategory || 'General',
          bookClassificationNo: bookClassificationNo || '',
          bookEdition: bookEdition || '1st',
          bookPages: String(bookPages || '0'),
          bookPrice: String(bookPrice || '0'),
          bookPublishYear: String(bookPublishYear || ''),
          bookPublisherName: (bookPublisherName || '').toUpperCase(),
          bookScope: bookScope || 'Circulation',
          bookLocation: bookLocation || "",
          bookStatus: bookStatus || 'Active',
          bookTitle: (bookTitle || '').toUpperCase(),
          updatedOn: todayDate,
          updatedBy: loggedUser.nickname || "Librarian",
          authorMark: authorMark || 'NA',
          bookAbstract: (bookAbstract || '').toUpperCase(),
          bookKeywords: (bookKeywords || '').toUpperCase(),
          bookCopies: String(bookCopies ?? '1'),
          bookPublisherLocation: (bookPublisherLocation || 'NA').toUpperCase(),
          bookSubTitle: (bookSubTitle || 'NA').toUpperCase(),
          bookDimension: bookDimension || 'NA',
          isbn: (isbn && isbn !== 'NA') ? isbn.toUpperCase() : 'NA'
        };

        await axios.patch(`${config.api.invokeURL}/vjlibrary/catalogue`, params, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

        if (oldBookDetails?.bookKeywords !== bookDetails?.bookKeywords) {
          const keywordpayload = {
            book: params,
            oldBook: oldBookDetails || "",
            accessionNumbers: [accessionNo]
          };
          await axios.post(`${config.api.invokeURL}/vjlibrary/catalogue/keyword`, keywordpayload, {
            headers: {
              Authorization: idToken,
              'x-api-key': config.api.key
            }
          });
        }

        if (imageFile) {
          postOnS3(accessionNo);
        }

        enqueueSnackbar(`Book Updated Successfully - Accession #${accessionNo}`, { variant: 'success' });
        handleClose();
        const updatedData = updateItemInArray("accessionNo", accessionNo, params);
        setSearchResult(updatedData);
        handleReset();
        setLoaded(true);
      } else {
        setLoaded(true);
      }
    } catch (error) {
      console.error("Error updating book:", error);
      const serverError = error.response?.data?.error || error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || error.message;
      enqueueSnackbar("Error Occurred: " + serverError, { variant: 'warning' });
      setLoaded(true);
    }
  };

  // Update Accession Number Submit
  const handleUpdateAccession = async (event) => {
    if (event) event.preventDefault();
    try {
      setLoaded(false);
      const validationResult = await validateFields();
      if (validationResult === "passed") {
        const { clientID, accessionNo, bookAuthor, registeredOn, bookCategory, bookClassificationNo, bookEdition, bookPages, bookPrice, bookPublishYear, bookPublisherName, bookScope, bookLocation, bookStatus, bookTitle, authorMark, bookCopies, bookPublisherLocation, bookSubTitle, bookKeywords, bookAbstract, bookDimension, isbn } = bookDetails;
        const todayDate = await generateDate();
        const { idToken } = (await fetchAuthSession()).tokens ?? {};

        const params = {
          clientID: clientID || "VJCollege",
          accessionNo: accessionNo,
          newAccessionNo: newAccessionNo,
          registeredOn: registeredOn || todayDate,
          bookAuthor: (bookAuthor || '').toUpperCase(),
          bookCategory: bookCategory,
          bookClassificationNo: bookClassificationNo,
          bookEdition: bookEdition,
          bookPages: String(bookPages || '0'),
          bookPrice: String(bookPrice || '0'),
          bookPublishYear: String(bookPublishYear || ''),
          bookPublisherName: (bookPublisherName || '').toUpperCase(),
          bookScope: bookScope,
          bookLocation: bookLocation || "",
          bookStatus: bookStatus,
          bookTitle: (bookTitle || '').toUpperCase(),
          updatedOn: todayDate,
          updatedBy: loggedUser.nickname || "Librarian",
          authorMark: authorMark || 'NA',
          bookAbstract: (bookAbstract || '').toUpperCase(),
          bookKeywords: (bookKeywords || '').toUpperCase(),
          bookCopies: String(bookCopies ?? '1'),
          bookPublisherLocation: (bookPublisherLocation || 'NA').toUpperCase(),
          bookSubTitle: (bookSubTitle || 'NA').toUpperCase(),
          bookDimension: bookDimension || 'NA',
          isbn: (isbn && isbn !== 'NA') ? isbn.toUpperCase() : 'NA'
        };

        await axios.patch(`${config.api.invokeURL}/vjlibrary/catalogue/accessionno`, params, {
          headers: {
            Authorization: idToken,
            'x-api-key': config.api.key
          }
        });

        if (imageFile) {
          postOnS3(newAccessionNo || accessionNo);
        }

        enqueueSnackbar(`Accession Number Re-assigned to ${newAccessionNo || accessionNo}`, { variant: 'success' });
        handleClose();
        const updatedData = updateItemInArray("accessionNo", accessionNo, { ...bookDetails, accessionNo: newAccessionNo || accessionNo });
        setSearchResult(updatedData);
        handleReset();
        setLoaded(true);
      } else {
        setLoaded(true);
      }
    } catch (error) {
      console.error("Error updating accession number:", error);
      const serverError = error.response?.data?.error || error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : null) || error.message;
      enqueueSnackbar("Error Occurred: " + serverError, { variant: 'warning' });
      setLoaded(true);
    }
  };

  function updateItemInArray(key, value, updatedFields) {
    return searchResult.map(item => {
      if (String(item[key]) === String(value)) {
        return { ...item, ...updatedFields };
      }
      return item;
    });
  }

  // S3 Image Upload
  const postOnS3 = async (taccessionNo) => {
    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const fileName = taccessionNo;
      const tnewFileName = fileName + ".jpeg";
      const keyName = 'library/poster/';
      const bucketName = config.s3.bucketName;

      const file = imageFile;
      const blob = file.slice(0, file.size, 'image/jpeg');
      const newFile = new File([blob], tnewFileName, { type: 'image/jpeg' });
      const fileType = "image/jpeg";

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/catalogue/poster?fileName=${tnewFileName}&bucketName=${bucketName}&fileType=${fileType}&keyName=${keyName}`, {
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const res = await fetch(response.data.uploadURL, {
        method: 'PUT',
        headers: {
          "Content-Type": "image/jpeg"
        },
        body: newFile
      });

      if (res.status === 200) {
        setProfileImage(newFileName);
      }
    } catch (error) {
      console.error("Error uploading poster to S3:", error);
      enqueueSnackbar("Error uploading image: " + error.message, { variant: 'warning' });
    } finally {
      setLoaded(true);
    }
  };

  const handlePreviewView = async (event) => {
    if (event) event.preventDefault();
    setLoaded(false);
    const validationResult = await validateFields();
    if (validationResult === "passed") {
      setPreviousFormType(formType);
      setPreviousModalTitle(modalTitle);
      setFormType("Preview");
      setModalTitle('Preview - Add Book');
      setViewOnly(true);
    }
    setLoaded(true);
  };

  const handleGoBack = (event) => {
    if (event) event.preventDefault();
    setFormType(previousFormType || "New");
    setModalTitle(previousModalTitle || 'Catalogue New Book');
    setViewOnly(false);
  };

  const handleClose = () => {
    setImageFile('');
    setShow(false);
  };

  const handleShow = (event, mode, item = null) => {
    if (event) event.preventDefault();
    setFormType(mode);
    setOldBookDetails({});
    if (mode === "View") {
      setViewOnly(true);
      setBookDetails(item);
      setModalTitle(`View Book Details - Accession #${item?.accessionNo || ''}`);
      const tImageName = `${item?.accessionNo}.jpeg`;
      setProfileImage(`${config.s3.bookPoster}${tImageName}`);
    } else if (mode === "Edit") {
      setViewOnly(false);
      const sanitized = {
        ...item,
        autoGenerate: false,
        authorMark: item?.authorMark || 'NA',
        bookCopies: item?.bookCopies || 1,
        bookSubTitle: item?.bookSubTitle || 'NA',
        bookPublisherLocation: item?.bookPublisherLocation || item?.publisherLocation || 'NA',
        bookLocation: item?.bookLocation || item?.liblocation || item?.location || '',
        isbn: item?.isbn || 'NA',
        bookKeywords: item?.bookKeywords || '',
        bookAbstract: item?.bookAbstract || '',
        bookDimension: item?.bookDimension || 'NA'
      };
      setBookDetails(sanitized);
      setOldBookDetails(sanitized);
      setModalTitle(`Edit Book - Accession #${item?.accessionNo || ''}`);
      const tImageName = `${item?.accessionNo}.jpeg`;
      setProfileImage(`${config.s3.bookPoster}${tImageName}`);
    } else if (mode === "New") {
      handleReset();
      setViewOnly(false);
      setModalTitle('Catalogue New Book');
      setFormType("New");
    } else if (mode === "Clone") {
      setViewOnly(false);
      const cloned = {
        ...item,
        autoGenerate: true,
        accessionNo: "",
        authorMark: item?.authorMark || 'NA',
        bookCopies: 1,
        bookSubTitle: item?.bookSubTitle || 'NA',
        bookPublisherLocation: item?.bookPublisherLocation || item?.publisherLocation || 'NA',
        bookLocation: item?.bookLocation || item?.liblocation || item?.location || '',
        isbn: item?.isbn || 'NA',
        bookKeywords: item?.bookKeywords || '',
        bookAbstract: item?.bookAbstract || '',
        bookDimension: item?.bookDimension || 'NA'
      };
      setBookDetails(cloned);
      setOldBookDetails({});
      setModalTitle(`Clone Book from [${item?.accessionNo}] ${item?.bookTitle}`);
      setFormType('Clone');
    } else if (mode === "Update") {
      setViewOnly(true);
      setBookDetails(item);
      setNewAccessionNo(item?.accessionNo);
      setModalTitle(`Update Accession Number: #${item?.accessionNo}`);
      const tImageName = `${item?.accessionNo}.jpeg`;
      setProfileImage(`${config.s3.bookPoster}${tImageName}`);
      setFormType("Update");
    }
    setShow(true);
  };

  // Open & Close Book Articles Modal
  const handleOpenArticlesModal = (event, item) => {
    if (event) event.preventDefault();
    setSelectedBookForArticles(item);
    setShowArticlesModal(true);
  };

  const handleCloseArticlesModal = () => {
    setShowArticlesModal(false);
    setSelectedBookForArticles(null);
  };

  // CSV & Print triggers
  const handleDownloadAll = (event) => {
    if (event) event.preventDefault();
    if (csvLinkEl.current?.link) {
      csvLinkEl.current.link.click();
    }
  };

  const handleDownloadSelected = (event) => {
    if (event) event.preventDefault();
    if (csvSelectedLinkEl.current?.link) {
      csvSelectedLinkEl.current.link.click();
    }
  };

  const printFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Vidyajyoti College - Book Catalogue (${new Date().toLocaleDateString()})`,
  });

  const processSelection = (ivalue) => {
    if (ivalue) {
      setSearchValue(ivalue.name);
    }
  };

  // Copy to clipboard helper
  const handleCopyText = (text, label, event) => {
    if (event) event.stopPropagation();
    if (text) {
      navigator.clipboard.writeText(String(text));
      enqueueSnackbar(`${label} "${text}" copied to clipboard!`, { variant: 'info' });
    }
  };

  const handleCopySuccessAcc = (text, label, event) => {
    handleCopyText(text, label, event);
    setCopiedAcc(String(text));
    setTimeout(() => {
      setCopiedAcc('');
    }, 2500);
  };

  const resetAllFilters = () => {
    setClientFilter('');
    setStatusFilter('ALL');
    setScopeFilter('ALL');
    setCategoryFilter('ALL');
    setLocationFilter('ALL');
    setCurrentPage(1);
  };

  const isFilterActive = clientFilter !== '' ||
    statusFilter !== 'ALL' ||
    scopeFilter !== 'ALL' ||
    categoryFilter !== 'ALL' ||
    locationFilter !== 'ALL';

  return (
    <>
      <Helmet>
        <title>Manage Books - Vidyajyoti College of Theology Library</title>
        <meta name="description" content="Catalogue books, manage accession numbers, track circulation status, and export library book records." />
      </Helmet>

      {!loaded && <Preloader show={true} />}

      <div className="addbooks-manage-container">
        {/* Top Header Card */}
        <div className="addbooks-manage-header d-flex flex-wrap justify-content-between align-items-center gap-3">
          <div>
            <div className="addbooks-manage-title d-flex align-items-center gap-2">
              <span className="addbooks-header-icon">
                <i className="bi bi-book"></i>
              </span>
              <span>Library Book Catalogue & Management</span>
            </div>
            <div className="addbooks-manage-subtitle">
              Catalogue new titles, assign accession numbers, track circulation status, and manage shelf locations.
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button
              className="btn-addbooks-primary"
              onClick={(e) => handleShow(e, "New")}
            >
              <i className="bi bi-plus-circle me-1"></i> Add New Book
            </Button>

            <ButtonGroup className="shadow-sm">
              <OverlayTrigger placement="bottom" overlay={<Tooltip>Print Filtered Books List</Tooltip>}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={printFn}
                  className="py-2 px-3 bg-white"
                >
                  <i className="bi bi-printer me-1"></i> Print
                </Button>
              </OverlayTrigger>

              <OverlayTrigger placement="bottom" overlay={<Tooltip>Export Filtered List to CSV</Tooltip>}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleDownloadAll}
                  className="py-2 px-3 bg-white"
                >
                  <i className="bi bi-file-earmark-spreadsheet me-1"></i> Export CSV
                </Button>
              </OverlayTrigger>
            </ButtonGroup>

            <CSVLink
              headers={downloaderBookinfo}
              filename={`VJ_Books_Catalogue_${new Date().toISOString().split('T')[0]}.csv`}
              data={processedBooks}
              ref={csvLinkEl}
              className="d-none"
            />

            <CSVLink
              headers={downloaderBookinfo}
              filename={`VJ_Books_Selected_${new Date().toISOString().split('T')[0]}.csv`}
              data={selectedBooksData}
              ref={csvSelectedLinkEl}
              className="d-none"
            />
          </div>
        </div>

        {/* Recently Catalogued Book Alert Banner */}
        {createdBookSuccess && (
          <div
            className="d-flex flex-wrap justify-content-between align-items-center p-3 mb-3 rounded-3 shadow-sm border border-success"
            style={{ background: '#f0fdf4' }}
          >
            <div className="d-flex align-items-center gap-3">
              <span className="badge bg-success rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}>
                <i className="bi bi-upc-scan fs-5 text-white"></i>
              </span>
              <div>
                <div className="fw-bold text-success mb-1 d-flex align-items-center gap-1">
                  <i className="bi bi-check-circle-fill"></i> Recently Catalogued Book
                </div>
                <div className="text-dark small">
                  <strong>{createdBookSuccess.bookTitle}</strong> — Assigned Accession No:
                  <span className="badge bg-success font-monospace ms-2 py-1 px-2 fs-6">
                    #{createdBookSuccess.accessionNumbers.join(', #')}
                  </span>
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="ms-2 py-0 px-2 fw-semibold"
                    onClick={(e) => handleCopySuccessAcc(createdBookSuccess.accessionNumbers.join(', '), "Accession No", e)}
                  >
                    <i className={`bi ${copiedAcc === createdBookSuccess.accessionNumbers.join(', ') ? 'bi-check2 text-success' : 'bi-clipboard'} me-1`}></i>
                    {copiedAcc === createdBookSuccess.accessionNumbers.join(', ') ? 'Copied!' : 'Copy'}
                  </Button>
                  {createdBookSuccess.bookClassificationNo && (
                    <span className="text-muted ms-2">
                      | Call No: <strong className="text-primary font-monospace">{createdBookSuccess.bookClassificationNo}</strong>
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
              <Button
                variant="outline-success"
                size="sm"
                onClick={() => setShowSuccessModal(true)}
                title="View full generated accession confirmation"
              >
                <i className="bi bi-info-circle me-1"></i> View Details
              </Button>
              <Link to="/barcode" className="btn btn-sm btn-outline-primary">
                <i className="bi bi-printer me-1"></i> Spine Labels
              </Link>
              <Button
                variant="success"
                size="sm"
                onClick={(e) => {
                  handleShow(e, "New");
                }}
              >
                <i className="bi bi-plus-circle me-1"></i> Add Another
              </Button>
              <button
                type="button"
                className="btn-close ms-1"
                onClick={() => setCreatedBookSuccess(null)}
                title="Dismiss"
              ></button>
            </div>
          </div>
        )}

        {/* KPI Metric Summary Cards */}
        <div className="addbooks-kpi-grid">
          <div
            className={`addbooks-kpi-card kpi-border-total ${statusFilter === 'ALL' && !isFilterActive ? 'kpi-card-active' : ''}`}
            onClick={resetAllFilters}
            title="Click to view all books"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="addbooks-kpi-label">Total Books Loaded</div>
              <div className="kpi-icon-pill bg-blue-soft"><i className="bi bi-collection text-primary"></i></div>
            </div>
            <div className="addbooks-kpi-value text-primary">{kpiStats.total}</div>
            <div className="addbooks-kpi-subtext">Matching search query</div>
          </div>

          <div
            className={`addbooks-kpi-card kpi-border-active ${statusFilter === 'Active' ? 'kpi-card-active' : ''}`}
            onClick={() => { setStatusFilter(statusFilter === 'Active' ? 'ALL' : 'Active'); setCurrentPage(1); }}
            title="Click to toggle Active circulation filter"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="addbooks-kpi-label">Active on Shelf</div>
              <div className="kpi-icon-pill bg-green-soft"><i className="bi bi-check-circle text-success"></i></div>
            </div>
            <div className="addbooks-kpi-value text-success">{kpiStats.active}</div>
            <div className="addbooks-kpi-subtext">{kpiStats.activePct}% available for issue</div>
          </div>

          <div
            className={`addbooks-kpi-card kpi-border-issued ${statusFilter === 'Issued' ? 'kpi-card-active' : ''}`}
            onClick={() => { setStatusFilter(statusFilter === 'Issued' ? 'ALL' : 'Issued'); setCurrentPage(1); }}
            title="Click to toggle Issued books filter"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="addbooks-kpi-label">Currently Issued</div>
              <div className="kpi-icon-pill bg-amber-soft"><i className="bi bi-person-check text-warning"></i></div>
            </div>
            <div className="addbooks-kpi-value text-warning">{kpiStats.issued}</div>
            <div className="addbooks-kpi-subtext">On loan to borrowers</div>
          </div>

          <div
            className={`addbooks-kpi-card kpi-border-reference ${scopeFilter === 'Reference' ? 'kpi-card-active' : ''}`}
            onClick={() => { setScopeFilter(scopeFilter === 'Reference' ? 'ALL' : 'Reference'); setCurrentPage(1); }}
            title="Click to toggle Reference Scope filter"
          >
            <div className="d-flex justify-content-between align-items-start">
              <div className="addbooks-kpi-label">Reference Collection</div>
              <div className="kpi-icon-pill bg-purple-soft"><i className="bi bi-bookmark-star text-purple"></i></div>
            </div>
            <div className="addbooks-kpi-value text-purple">{kpiStats.reference}</div>
            <div className="addbooks-kpi-subtext">Restricted reference copies</div>
          </div>
        </div>

        {/* Search & Discovery Panel */}
        <div className="addbooks-search-panel">
          {/* Quick Category Chips */}
          <div className="addbooks-chips-bar d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="small fw-bold text-muted me-1">
              <i className="bi bi-lightning-charge-fill text-warning me-1"></i>Quick Browse:
            </span>
            <button
              type="button"
              className={`addbooks-quick-chip ${searchValue === 'Theology' && searchTopic === 'BookCategory' ? 'chip-selected' : ''}`}
              onClick={() => handleQuickCategory('BookCategory', 'THEOLOGY')}
            >
              <i className="bi bi-book-half me-1"></i> Theology
            </button>
            <button
              type="button"
              className="addbooks-quick-chip"
              onClick={() => handleQuickCategory('BookCategory', 'PHILOSOPHY')}
            >
              <i className="bi bi-mortarboard me-1"></i> Philosophy
            </button>
            <button
              type="button"
              className="addbooks-quick-chip"
              onClick={() => handleQuickCategory('BookCategory', 'LITURGY')}
            >
              <i className="bi bi-bookmark-star me-1"></i> Liturgy
            </button>
            <button
              type="button"
              className="addbooks-quick-chip"
              onClick={() => handleQuickCategory('BookCategory', 'CHURCH HISTORY')}
            >
              <i className="bi bi-hourglass-split me-1"></i> Church History
            </button>
            <button
              type="button"
              className="addbooks-quick-chip"
              onClick={() => handleQuickCategory('BookCategory', 'SOCIETY OF JESUS')}
            >
              <i className="bi bi-compass me-1"></i> Society Of Jesus
            </button>
            <button
              type="button"
              className="addbooks-quick-chip"
              onClick={() => handleQuickCategory('BookTitle', 'SPIRITUAL EXERCISES')}
            >
              <i className="bi bi-bookshelf me-1"></i> Spiritual Exercises
            </button>
          </div>

          {/* Search Form */}
          <Form onSubmit={handleSearch}>
            <Row className="g-3 align-items-center">
              <Col lg={3} md={4} sm={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted mb-1">Search Field</Form.Label>
                  <Form.Select
                    className="addbooks-select"
                    value={searchTopic}
                    onChange={(e) => setSearchTopic(e.target.value)}
                  >
                    <option value="AccessionNo">Book ID / Accession No</option>
                    <option value="BookTitle">Book Title</option>
                    <option value="BookAuthor">Author</option>
                    <option value="Call No">Call Number</option>
                    <option value="BookCategory">Category</option>
                    <option value="BookPublisher">Publisher</option>
                    <option value="PublishingYear">Publish Year</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col lg={6} md={5} sm={12}>
                <Form.Group>
                  <Form.Label className="small fw-bold text-muted mb-1">Search Keywords</Form.Label>
                  {searchTopic === "BookCategory" ? (
                    <AutoComplete
                      data={categoryList}
                      onSelect={(cat) => processSelection(cat)}
                      placeholder="Type or select a category..."
                      item={searchValue}
                      onChange={(val) => setSearchValue(val)}
                    />
                  ) : (
                    <InputGroup>
                      <InputGroup.Text className="bg-light border-end-0">
                        <i className="bi bi-search text-muted"></i>
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        className="addbooks-search-input border-start-0"
                        placeholder={`Enter ${searchTopic.replace('Book', 'Book ')}...`}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                      />
                      {searchValue && (
                        <Button variant="outline-secondary" onClick={() => setSearchValue('')}>
                          <i className="bi bi-x-lg"></i>
                        </Button>
                      )}
                    </InputGroup>
                  )}
                </Form.Group>
              </Col>

              <Col lg={3} md={3} sm={12} className="d-flex align-items-end">
                <Button type="submit" className="btn-addbooks-search w-100 py-2">
                  <i className="bi bi-arrow-repeat me-1"></i> Search Catalogue
                </Button>
              </Col>
            </Row>
          </Form>

          {/* Real-time In-Page Filters */}
          {searchResult.length > 0 && (
            <div className="addbooks-filters-bar mt-3 pt-3 border-top">
              <Row className="g-2 align-items-center">
                {/* Live Keyword Filter */}
                <Col xl={3} lg={4} md={6} sm={12}>
                  <InputGroup size="sm">
                    <InputGroup.Text className="bg-white">
                      <i className="bi bi-funnel text-muted"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Filter title, author, ID, Call No..."
                      value={clientFilter}
                      onChange={(e) => {
                        setClientFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                    {clientFilter && (
                      <Button variant="outline-secondary" onClick={() => setClientFilter('')}>
                        <i className="bi bi-x"></i>
                      </Button>
                    )}
                  </InputGroup>
                </Col>

                {/* Status Pills */}
                <Col xl={4} lg={4} md={6} sm={12}>
                  <div className="d-flex flex-wrap align-items-center gap-1">
                    <span className="small text-muted me-1 fw-semibold">Status:</span>
                    <span
                      className={`status-pill ${statusFilter === 'ALL' ? 'active' : ''}`}
                      onClick={() => { setStatusFilter('ALL'); setCurrentPage(1); }}
                    >
                      All ({searchResult.length})
                    </span>
                    <span
                      className={`status-pill status-pill-active ${statusFilter === 'Active' ? 'active' : ''}`}
                      onClick={() => { setStatusFilter('Active'); setCurrentPage(1); }}
                    >
                      Active ({kpiStats.active})
                    </span>
                    <span
                      className={`status-pill status-pill-issued ${statusFilter === 'Issued' ? 'active' : ''}`}
                      onClick={() => { setStatusFilter('Issued'); setCurrentPage(1); }}
                    >
                      Issued ({kpiStats.issued})
                    </span>
                    <span
                      className={`status-pill status-pill-lost ${statusFilter === 'Lost' ? 'active' : ''}`}
                      onClick={() => { setStatusFilter('Lost'); setCurrentPage(1); }}
                    >
                      Lost
                    </span>
                  </div>
                </Col>

                {/* Scope Filter */}
                <Col xl={2} lg={2} md={4} sm={6}>
                  <Form.Select
                    size="sm"
                    value={scopeFilter}
                    onChange={(e) => { setScopeFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="ALL">All Scopes</option>
                    <option value="Circulation">Circulation</option>
                    <option value="Reference">Reference</option>
                  </Form.Select>
                </Col>

                {/* Category Dropdown */}
                {uniqueCategories.length > 0 && (
                  <Col xl={2} lg={2} md={4} sm={6}>
                    <Form.Select
                      size="sm"
                      value={categoryFilter}
                      onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}
                    >
                      <option value="ALL">All Categories</option>
                      {uniqueCategories.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </Form.Select>
                  </Col>
                )}

                {/* Reset Filters */}
                {isFilterActive && (
                  <Col xl={1} lg={12} md={12} className="text-end">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger p-0 text-decoration-none fw-bold small"
                      onClick={resetAllFilters}
                    >
                      <i className="bi bi-arrow-counterclockwise me-1"></i>Reset
                    </Button>
                  </Col>
                )}
              </Row>
            </div>
          )}
        </div>

        {/* Floating Batch Action Toolbar when items are selected */}
        {selectedAccessions.size > 0 && (
          <div className="addbooks-batch-toolbar d-flex flex-wrap justify-content-between align-items-center gap-2 shadow-lg mb-3">
            <div className="d-flex align-items-center gap-2">
              <span className="badge bg-primary fs-6 px-3 py-2">
                <i className="bi bi-check2-circle me-1"></i> {selectedAccessions.size} Selected
              </span>
              <span className="small text-muted">
                Action on selected book records:
              </span>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                className="bg-white"
                onClick={handleDownloadSelected}
              >
                <i className="bi bi-file-earmark-spreadsheet me-1"></i> Export Selected ({selectedAccessions.size})
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="bg-white"
                onClick={() => setSelectedAccessions(new Set())}
              >
                <i className="bi bi-x-circle me-1"></i> Clear Selection
              </Button>
            </div>
          </div>
        )}

        {/* Message / Results Meta Indicator */}
        {dispMsg && (
          <div className="d-flex justify-content-between align-items-center mb-2 px-1">
            <span className="text-muted small fw-bold">
              <i className="bi bi-info-circle me-1 text-primary"></i> {dispMsg}
              {isFilterActive && (
                <span className="text-primary ms-2">
                  (Showing {processedBooks.length} filtered results)
                </span>
              )}
            </span>
            {processedBooks.length > 0 && (
              <span className="text-muted small">
                Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, processedBooks.length)} of {processedBooks.length} books
              </span>
            )}
          </div>
        )}

        {/* Main Books Table */}
        {searchResult.length === 0 ? (
          <div className="addbooks-table-card addbooks-empty-state">
            <div className="addbooks-empty-icon">
              <i className="bi bi-book"></i>
            </div>
            <div className="addbooks-empty-title">
              {hasSearched ? "No Books Found" : "Loading Books Catalogue..."}
            </div>
            <div className="addbooks-empty-text">
              {hasSearched
                ? "No book records match your search criteria. Try searching by title, author, accession number, or category."
                : "Looking up the library's catalogued book collection..."}
            </div>
            <div className="d-flex justify-content-center gap-2">
              <Button className="btn-addbooks-primary" onClick={(e) => handleShow(e, "New")}>
                <i className="bi bi-plus-lg me-1"></i> Catalogue New Book
              </Button>
              <Button variant="outline-primary" onClick={() => executeSearch('BookCategory', 'Theology')}>
                <i className="bi bi-arrow-repeat me-1"></i> Browse Theology Books
              </Button>
            </div>
          </div>
        ) : processedBooks.length === 0 ? (
          <div className="addbooks-table-card addbooks-empty-state">
            <div className="addbooks-empty-icon text-muted">
              <i className="bi bi-funnel"></i>
            </div>
            <div className="addbooks-empty-title">No Matching Books</div>
            <div className="addbooks-empty-text">
              No book records match your active filters. Adjust your search keywords or reset all filters.
            </div>
            <Button
              variant="outline-primary"
              onClick={resetAllFilters}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i> Reset All Filters
            </Button>
          </div>
        ) : (
          <div className="addbooks-table-card">
            <div className="table-responsive">
              <Table hover className="addbooks-main-table align-middle" ref={componentRef}>
                <thead>
                  <tr>
                    <th style={{ width: '40px' }} className="text-center">
                      <Form.Check
                        type="checkbox"
                        checked={paginatedBooks.length > 0 && paginatedBooks.every(b => selectedAccessions.has(b.accessionNo))}
                        onChange={handleToggleSelectAll}
                        title="Select all on current page"
                      />
                    </th>
                    <th style={{ width: '50px' }}>#</th>
                    <th className="sortable-th" onClick={() => handleSortKey('AccessionNo')} style={{ minWidth: '130px' }}>
                      Accession #
                      {sorting.field === 'AccessionNo' && (
                        <i className={`bi bi-arrow-${sorting.order === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSortKey('BookTitle')} style={{ minWidth: '280px' }}>
                      Book Title & Details
                      {sorting.field === 'BookTitle' && (
                        <i className={`bi bi-arrow-${sorting.order === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSortKey('BookAuthor')} style={{ minWidth: '150px' }}>
                      Author
                      {sorting.field === 'BookAuthor' && (
                        <i className={`bi bi-arrow-${sorting.order === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSortKey('CallNo')} style={{ minWidth: '110px' }}>
                      Call No
                      {sorting.field === 'CallNo' && (
                        <i className={`bi bi-arrow-${sorting.order === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="sortable-th" onClick={() => handleSortKey('bookCategory')} style={{ minWidth: '130px' }}>
                      Category
                      {sorting.field === 'bookCategory' && (
                        <i className={`bi bi-arrow-${sorting.order === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th style={{ minWidth: '110px' }}>Scope</th>
                    <th style={{ minWidth: '120px' }}>Shelf Location</th>
                    <th className="sortable-th text-center" onClick={() => handleSortKey('bookStatus')} style={{ minWidth: '100px' }}>
                      Status
                      {sorting.field === 'bookStatus' && (
                        <i className={`bi bi-arrow-${sorting.order === 'asc' ? 'up' : 'down'} ms-1 text-primary`}></i>
                      )}
                    </th>
                    <th className="text-end" style={{ minWidth: '160px' }}>Quick Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBooks.map((item, index) => {
                    const rowNumber = ((currentPage - 1) * pageSize) + index + 1;
                    const status = item.bookStatus || 'Active';
                    const statusClass =
                      status === 'Active'
                        ? 'badge-book-active'
                        : status === 'Issued'
                          ? 'badge-book-issued'
                          : status === 'Lost'
                            ? 'badge-book-lost'
                            : status === 'Damaged'
                              ? 'badge-book-damaged'
                              : 'badge-book-active';

                    const isSelected = selectedAccessions.has(item.accessionNo);
                    const shelfLoc = item.bookLocation || item.liblocation || item.location;

                    return (
                      <tr key={item.accessionNo || index} className={isSelected ? 'table-selected-row' : ''}>
                        <td className="text-center">
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelectOne(item.accessionNo)}
                          />
                        </td>
                        <td className="text-muted small">{rowNumber}</td>
                        <td>
                          <div className="d-flex flex-column align-items-start gap-1">
                            <OverlayTrigger placement="right" overlay={<Tooltip>Click to copy Accession No</Tooltip>}>
                              <span
                                className="accession-badge"
                                onClick={(e) => handleCopyText(item.accessionNo, "Accession #", e)}
                              >
                                #{item.accessionNo}
                              </span>
                            </OverlayTrigger>
                            {item.isbn && item.isbn !== 'NA' && (
                              <span className="isbn-tag" title="ISBN Number">
                                ISBN: {item.isbn}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="book-title-cell">
                            <div className="fw-bold text-dark book-row-title" onClick={(e) => handleShow(e, "View", item)}>
                              {item.bookTitle}
                            </div>
                            {item.bookSubTitle && (
                              <div className="small text-muted text-truncate" style={{ maxWidth: 320 }}>
                                {item.bookSubTitle}
                              </div>
                            )}
                            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                              {item.bookPublisherName && (
                                <span className="small text-secondary">
                                  <i className="bi bi-building me-1"></i> {item.bookPublisherName}
                                </span>
                              )}
                              {item.bookPublishYear && (
                                <span className="small text-muted">
                                  ({item.bookPublishYear})
                                </span>
                              )}
                              {item.bookEdition && item.bookEdition !== 'NA' && (
                                <span className="small text-muted">
                                  • Ed: {item.bookEdition}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="fw-semibold text-dark">{item.bookAuthor}</div>
                          {item.authorMark && item.authorMark !== 'NA' && (
                            <div className="small text-muted">Mark: {item.authorMark}</div>
                          )}
                        </td>
                        <td>
                          {item.bookClassificationNo ? (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Click to copy Call Number</Tooltip>}>
                              <span
                                className="callno-badge"
                                onClick={(e) => handleCopyText(item.bookClassificationNo, "Call Number", e)}
                              >
                                {item.bookClassificationNo}
                              </span>
                            </OverlayTrigger>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td>
                          <span className="category-tag">
                            {item.bookCategory || 'General'}
                          </span>
                        </td>
                        <td>
                          <span className={`scope-badge ${item.bookScope === 'Reference' ? 'scope-reference' : 'scope-circulation'}`}>
                            {item.bookScope === 'Reference' ? (
                              <><i className="bi bi-lock me-1"></i> Reference</>
                            ) : (
                              <><i className="bi bi-arrow-repeat me-1"></i> Circulation</>
                            )}
                          </span>
                        </td>
                        <td>
                          {shelfLoc ? (
                            <span className="shelf-location-tag">
                              <i className="bi bi-geo-alt-fill me-1 text-danger"></i> {shelfLoc}
                            </span>
                          ) : (
                            <span className="text-muted small">-</span>
                          )}
                        </td>
                        <td className="text-center">
                          <span className={statusClass}>
                            {status === 'Active' && <span className="status-indicator-dot"></span>}
                            {status}
                          </span>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end align-items-center gap-1">
                            {/* Fast Direct Action: View */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>View Book Details</Tooltip>}>
                              <Button
                                variant="light"
                                size="sm"
                                className="btn-table-action action-view"
                                onClick={(e) => handleShow(e, "View", item)}
                              >
                                <i className="bi bi-eye"></i>
                              </Button>
                            </OverlayTrigger>

                            {/* Fast Direct Action: Edit */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit Book Info</Tooltip>}>
                              <Button
                                variant="light"
                                size="sm"
                                className="btn-table-action action-edit"
                                onClick={(e) => handleShow(e, "Edit", item)}
                              >
                                <i className="bi bi-pencil-square"></i>
                              </Button>
                            </OverlayTrigger>

                            {/* Fast Direct Action: Clone */}
                            <OverlayTrigger placement="top" overlay={<Tooltip>Clone as New Copy</Tooltip>}>
                              <Button
                                variant="light"
                                size="sm"
                                className="btn-table-action action-clone"
                                onClick={(e) => handleShow(e, "Clone", item)}
                              >
                                <i className="bi bi-copy"></i>
                              </Button>
                            </OverlayTrigger>

                            {/* Dropdown for More Actions */}
                            <Dropdown align="end">
                              <Dropdown.Toggle
                                variant="light"
                                size="sm"
                                className="btn-table-action border-0 bg-transparent text-secondary p-1"
                                id={`dropdown-book-${index}`}
                              >
                                <i className="bi bi-three-dots-vertical"></i>
                              </Dropdown.Toggle>

                              <Dropdown.Menu className="addbooks-action-menu shadow-lg">
                                <Dropdown.Item onClick={(e) => handleShow(e, "View", item)}>
                                  <i className="bi bi-eye text-primary"></i> Full Book Details
                                </Dropdown.Item>
                                <Dropdown.Item onClick={(e) => handleShow(e, "Edit", item)}>
                                  <i className="bi bi-pencil-square text-warning"></i> Edit Book Metadata
                                </Dropdown.Item>
                                <Dropdown.Item onClick={(e) => handleShow(e, "Clone", item)}>
                                  <i className="bi bi-copy text-success"></i> Clone / New Copy
                                </Dropdown.Item>
                                <Dropdown.Item onClick={(e) => handleOpenArticlesModal(e, item)}>
                                  <i className="bi bi-file-earmark-text text-indigo"></i> View Articles
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={(e) => handleShow(e, "Update", item)}>
                                  <i className="bi bi-upc-scan text-indigo"></i> Re-assign Accession #
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="pagination-bar">
                <div className="d-flex align-items-center gap-2">
                  <span className="small text-muted">Rows per page:</span>
                  <Form.Select
                    size="sm"
                    style={{ width: '80px' }}
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </div>

                <Pagination size="sm" className="mb-0">
                  <Pagination.First
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  />

                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum = i + 1;
                    if (totalPages > 5 && currentPage > 3) {
                      pageNum = currentPage - 3 + i;
                      if (pageNum > totalPages) pageNum = totalPages - (4 - i);
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === currentPage}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}

                  <Pagination.Next
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  />
                  <Pagination.Last
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ================= MODAL: BOOK VIEW / EDIT / ADD / PREVIEW / ACCESSION ================= */}
      <Modal show={show} onHide={handleClose} size="xl" centered backdrop="static" className="addbooks-custom-modal">
        <Modal.Header closeButton className="modal-custom-header">
          <Modal.Title className="h5 fw-bold text-white mb-0">
            <i className="bi bi-book me-2"></i>
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-white">
          {formType === "Preview" ? (
            <BooksPreview
              bookDetails={bookDetails}
              setBookDetails={setBookDetails}
              viewOnly={viewOnly}
              handleClose={handleClose}
              dispMsg={dispMsg}
              handleGoBack={handleGoBack}
            />
          ) : (
            <BooksForm
              bookDetails={bookDetails}
              setBookDetails={setBookDetails}
              viewOnly={viewOnly}
              handleClose={handleClose}
              formType={formType}
              dispMsg={dispMsg}
              setNewFileName={setNewFileName}
              setImageFile={setImageFile}
              profileImage={profileImage}
              newAccessionNo={newAccessionNo}
              setNewAccessionNo={setNewAccessionNo}
            />
          )}

          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-4 pt-3 border-top">
            <div>
              {formType === "Preview" && (
                <Button variant="outline-primary" onClick={handleGoBack} className="fw-semibold">
                  <i className="bi bi-arrow-left me-1"></i> Back to Edit Details
                </Button>
              )}
            </div>

            <div className="d-flex align-items-center gap-2">
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>

              {formType === "New" || formType === "Clone" ? (
                <Button className="btn-addbooks-primary" onClick={handlePreviewView}>
                  <i className="bi bi-eye me-1"></i> Preview & Verify
                </Button>
              ) : formType === "Preview" ? (
                <Button className="btn-addbooks-primary" onClick={handleNewSubmit}>
                  <i className="bi bi-check2-circle me-1"></i> Save & Catalogue
                </Button>
              ) : formType === "Edit" ? (
                <Button className="btn-addbooks-primary" onClick={handleEditSubmit}>
                  <i className="bi bi-check2 me-1"></i> Save Changes
                </Button>
              ) : formType === "Update" ? (
                <Button className="btn-addbooks-primary" onClick={handleUpdateAccession}>
                  <i className="bi bi-upc-scan me-1"></i> Update Accession #
                </Button>
              ) : null}
            </div>
          </div>
        </Modal.Body>
      </Modal>

      {/* ================= MODAL: BOOK ARTICLES & CHAPTERS INDEX ================= */}
      <Modal
        show={showArticlesModal}
        onHide={handleCloseArticlesModal}
        size="xl"
        centered
        backdrop="static"
        className="addbooks-custom-modal"
      >
        <Modal.Header closeButton className="modal-custom-header">
          <Modal.Title className="h5 fw-bold text-white mb-0 d-flex align-items-center gap-2">
            <i className="bi bi-journal-bookmark-fill"></i>
            {selectedBookForArticles
              ? `Articles & Chapters — [${selectedBookForArticles.accessionNo}] ${selectedBookForArticles.bookTitle}`
              : 'Book Articles Index'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-3 bg-light">
          {selectedBookForArticles && (
            <ListBookArticles
              bookDetails={selectedBookForArticles}
              handleParentClose={handleCloseArticlesModal}
            />
          )}
        </Modal.Body>
        <Modal.Footer className="bg-white border-top py-2 px-3">
          <Button variant="secondary" onClick={handleCloseArticlesModal}>
            Close Articles Manager
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ================= MODAL: NEW ACCESSION NUMBER SUCCESS CONFIRMATION ================= */}
      <Modal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        centered
        size="lg"
        backdrop="static"
        className="addbooks-custom-modal"
      >
        <Modal.Header closeButton className="bg-success text-white">
          <Modal.Title className="h5 fw-bold text-white mb-0 d-flex align-items-center gap-2">
            <i className="bi bi-check-circle-fill"></i>
            Book Catalogued Successfully
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 bg-white">
          {createdBookSuccess && (
            <div>
              {/* Highlight Card for Accession Number(s) */}
              <div
                className="p-4 mb-4 rounded-3 text-center"
                style={{
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                  border: '2px solid #86efac'
                }}
              >
                <div className="small text-success fw-bold text-uppercase tracking-wider mb-2">
                  <i className="bi bi-upc-scan me-1"></i>
                  {createdBookSuccess.accessionNumbers.length > 1
                    ? `Generated Accession Numbers (${createdBookSuccess.accessionNumbers.length} Copies)`
                    : 'Generated Accession Number'}
                </div>

                <div className="d-flex flex-wrap justify-content-center align-items-center gap-3 my-2">
                  {createdBookSuccess.accessionNumbers.map((acc, idx) => (
                    <div
                      key={acc || idx}
                      className="d-inline-flex align-items-center gap-2 bg-white px-3 py-2 rounded-3 shadow-sm border border-success"
                    >
                      <span
                        className="fs-3 fw-bolder font-monospace text-success"
                        style={{ letterSpacing: '1px' }}
                      >
                        #{acc}
                      </span>
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="d-flex align-items-center gap-1 py-1 px-2"
                        onClick={(e) => handleCopySuccessAcc(acc, "Accession No", e)}
                        title="Copy Accession Number to Clipboard"
                      >
                        <i className={`bi ${copiedAcc === String(acc) ? 'bi-check2 text-success' : 'bi-clipboard'}`}></i>
                        {copiedAcc === String(acc) ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                  ))}
                </div>

                {createdBookSuccess.accessionNumbers.length > 1 && (
                  <div className="mt-2">
                    <Button
                      variant="link"
                      size="sm"
                      className="text-success text-decoration-none fw-bold"
                      onClick={(e) => handleCopySuccessAcc(createdBookSuccess.accessionNumbers.join(', '), "All Accession Numbers", e)}
                    >
                      <i className={`bi ${copiedAcc === createdBookSuccess.accessionNumbers.join(', ') ? 'bi-check2' : 'bi-clipboard-check'} me-1`}></i>
                      {copiedAcc === createdBookSuccess.accessionNumbers.join(', ') ? 'All Copied!' : 'Copy All Accession Numbers'}
                    </Button>
                  </div>
                )}

                {/* Librarian Action Notice */}
                <div
                  className="mt-3 p-3 rounded-2 text-start small d-flex align-items-start gap-2"
                  style={{ background: '#ffffffcc', border: '1px dashed #22c55e', color: '#166534' }}
                >
                  <i className="bi bi-pencil-square fs-5 text-success mt-0"></i>
                  <div>
                    <strong>Action for Librarian:</strong> Please note and record the assigned accession number{' '}
                    <span className="fw-bold font-monospace bg-white px-1 border border-success rounded">
                      #{createdBookSuccess.accessionNumbers.join(', #')}
                    </span>{' '}
                    on the physical book (e.g. back of title page, spine label, or pocket) and in your library register before shelving.
                  </div>
                </div>
              </div>

              {/* Book Metadata Snapshot */}
              <div className="bg-light p-3 rounded-3 border">
                <div className="fw-bold text-dark fs-6 mb-2">
                  {createdBookSuccess.bookTitle}
                </div>
                <Row className="g-2 small">
                  <Col sm={6}>
                    <div className="text-muted">Author:</div>
                    <div className="fw-semibold text-dark">{createdBookSuccess.bookAuthor || 'N/A'}</div>
                  </Col>
                  <Col sm={6}>
                    <div className="text-muted">Dewey Call No / Classification:</div>
                    <div className="fw-semibold text-primary font-monospace">
                      {createdBookSuccess.bookClassificationNo || 'N/A'}
                    </div>
                  </Col>
                  <Col sm={6}>
                    <div className="text-muted">Subject Category:</div>
                    <div className="fw-semibold text-dark">{createdBookSuccess.bookCategory || 'General'}</div>
                  </Col>
                  <Col sm={6}>
                    <div className="text-muted">Shelf Location / Scope:</div>
                    <div className="fw-semibold text-dark">
                      {createdBookSuccess.bookLocation || 'Periodical / Main Stack'} ({createdBookSuccess.bookScope || 'Circulation'})
                    </div>
                  </Col>
                  {createdBookSuccess.bookPublisherName && (
                    <Col sm={6}>
                      <div className="text-muted">Publisher:</div>
                      <div className="fw-semibold text-dark">
                        {createdBookSuccess.bookPublisherName} {createdBookSuccess.bookPublishYear ? `(${createdBookSuccess.bookPublishYear})` : ''}
                      </div>
                    </Col>
                  )}
                  {createdBookSuccess.isbn && createdBookSuccess.isbn !== 'NA' && (
                    <Col sm={6}>
                      <div className="text-muted">ISBN:</div>
                      <div className="fw-semibold text-dark font-monospace">{createdBookSuccess.isbn}</div>
                    </Col>
                  )}
                </Row>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Link
            to="/barcode"
            className="btn btn-outline-primary d-inline-flex align-items-center gap-1"
          >
            <i className="bi bi-printer me-1"></i> Print Barcode & Spine Labels
          </Link>

          <div className="d-flex align-items-center gap-2">
            <Button
              className="btn-addbooks-primary d-inline-flex align-items-center gap-1"
              onClick={() => {
                setShowSuccessModal(false);
                handleShow(null, "New");
              }}
            >
              <i className="bi bi-plus-circle me-1"></i> Catalogue Another Book
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowSuccessModal(false)}
            >
              Done & View Catalogue
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Scroll to top button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="scroll-top-btn"
          title="Scroll to Top"
        >
          <i className="bi bi-arrow-up"></i>
        </button>
      )}
    </>
  );
}
